import React, { useState, useRef, useEffect } from 'react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { PremiumDoughnut, PremiumBarChart, RankedList } from './charts';

interface ReportPreviewEditorProps {
  isOpen: boolean;
  reportData: {
    projectName: string;
    dateGenerated: string;
    candidatesCount: number;
    companiesCount: number;
    totalCandidates: number;
    totalCompanies: number;
    hasFilters: boolean;
    filterSummary: string;
    additionalContext: string;
    sections: ReportSection[];
    careerProgression: {
      avgTenure: string;
      avgExperience: string;
    };
  };
  chartImages: { [key: string]: string }; // Base64 chart images
  onClose: () => void;
  onDownload: () => void;
}

interface ReportSection {
  title: string;
  chartKey: string;
  insights: string[];
  enabled: boolean;
  chartType?: 'doughnut' | 'bar' | 'ranked';
  chartData?: { label: string; value: number; subtitle?: string }[];
  centerLabel?: string;
}

const ReportPreviewEditor: React.FC<ReportPreviewEditorProps> = ({
  isOpen,
  reportData,
  chartImages,
  onClose,
  onDownload
}) => {
  const [sections, setSections] = useState<ReportSection[]>(reportData.sections);
  const [additionalContext, setAdditionalContext] = useState(reportData.additionalContext);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateInsight = (sectionIdx: number, insightIdx: number, newText: string) => {
    const newSections = [...sections];
    newSections[sectionIdx].insights[insightIdx] = newText;
    setSections(newSections);
  };

  const deleteInsight = (sectionIdx: number, insightIdx: number) => {
    const newSections = [...sections];
    newSections[sectionIdx].insights = newSections[sectionIdx].insights.filter((_, idx) => idx !== insightIdx);
    setSections(newSections);
  };

  const deleteSection = (chartKey: string) => {
    const newSections = sections.map(s =>
      s.chartKey === chartKey ? { ...s, enabled: false } : s
    );
    setSections(newSections);
  };

  const deleteLogo = () => {
    setLogoUrl('');
  };

  const formatCareerVelocity = (value: string) => {
    // Parse the number from the string (e.g., "11.76923076923077 years" -> "11.7 years")
    const match = value.match(/^([\d.]+)\s*(.*)$/);
    if (match) {
      const number = parseFloat(match[1]);
      const unit = match[2];
      return `${number.toFixed(1)} ${unit}`;
    }
    return value;
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    setDownloading(true);
    try {
      // Hide all delete buttons during PDF generation
      const deleteButtons = reportRef.current.querySelectorAll('.delete-button');
      deleteButtons.forEach(btn => {
        (btn as HTMLElement).style.display = 'none';
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Get all pages
      const pages = reportRef.current.querySelectorAll('.pdf-page');

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        const page = pages[i] as HTMLElement;

        // html-to-image uses SVG foreignObject which preserves the browser's
        // actual CSS rendering — no text clipping, correct gradients, proper flex.
        const imgData = await toPng(page, {
          pixelRatio: 2,
          backgroundColor: '#ffffff',
        });

        // Load image to get dimensions for aspect ratio calculation
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = imgData;
        });

        const imgWidth = pageWidth;
        const imgHeight = (img.naturalHeight * pageWidth) / img.naturalWidth;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }

      const filename = `Market_Intelligence_${reportData.projectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      // Show delete buttons again
      deleteButtons.forEach(btn => {
        (btn as HTMLElement).style.display = '';
      });

      onDownload();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');

      // Show delete buttons again in case of error
      const deleteButtons = reportRef.current?.querySelectorAll('.delete-button');
      deleteButtons?.forEach(btn => {
        (btn as HTMLElement).style.display = '';
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50 overflow-hidden">
      <style>{`
        .editable-hover:hover {
          background-color: #FEF3C7;
          outline: 1px dashed #F59E0B;
        }
        .delete-button {
          opacity: 0;
          transition: opacity 0.2s;
          cursor: pointer;
          background: #EF4444;
          color: white;
          border: none;
          border-radius: 3px;
          padding: 2px 6px;
          font-size: 11px;
          font-weight: bold;
        }
        .delete-button:hover {
          background: #DC2626;
        }
        .section-header:hover .delete-button,
        .insight-row:hover .delete-button,
        .logo-container:hover .delete-button {
          opacity: 1;
        }
      `}</style>
      <div className="w-full h-full flex flex-col">
        {/* Sticky Header */}
        <div className="bg-white border-b shadow-sm px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-gray-900">Report Preview & Editor</h2>
            <span className="text-sm text-gray-600">Click any text to edit • Upload logo</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Logo Upload */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
              >
                <span>🖼️</span>
                <span>{logoUrl ? 'Change Logo' : 'Upload Logo'}</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>

            <button
              onClick={onClose}
              disabled={downloading}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2 font-medium"
            >
              {downloading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>📥</span>
                  <span>Download PDF</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scrollable Report Preview */}
        <div className="flex-1 overflow-y-auto bg-gray-200 p-8">
          <div ref={reportRef} className="max-w-4xl mx-auto space-y-8">
            {/* PAGE 1: Cover */}
            <div className="pdf-page bg-white shadow-lg" style={{ width: '210mm', minHeight: '297mm', padding: '15mm', position: 'relative' }}>
              {/* Top accent bar */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4mm', backgroundColor: '#3B82F6' }}></div>

              {/* Logo */}
              {logoUrl && (
                <div className="logo-container" style={{
                  position: 'absolute',
                  top: '50mm',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  maxWidth: '60mm',
                  maxHeight: '25mm',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <img src={logoUrl} alt="Company Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  <button
                    onClick={deleteLogo}
                    className="delete-button"
                    style={{ position: 'absolute', top: '-5px', right: '-5px' }}
                    title="Remove logo"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Decorative circles */}
              <svg style={{ position: 'absolute', right: '10mm', top: '30mm', opacity: 0.1 }} width="80" height="120">
                <circle cx="40" cy="25" r="25" stroke="#E5E7EB" strokeWidth="1" fill="none" />
                <circle cx="40" cy="55" r="30" stroke="#E5E7EB" strokeWidth="1" fill="none" />
                <circle cx="40" cy="90" r="35" stroke="#E5E7EB" strokeWidth="1" fill="none" />
              </svg>

              {/* Title */}
              <div style={{ marginTop: '80mm', textAlign: 'center' }}>
                <h1 style={{ fontSize: '32pt', fontWeight: 'bold', color: '#1F2937', marginBottom: '5mm' }}>
                  Market Intelligence
                </h1>
                <h1 style={{ fontSize: '32pt', fontWeight: 'bold', color: '#3B82F6', marginBottom: '10mm' }}>
                  Report
                </h1>
                <p style={{ fontSize: '14pt', color: '#4B5563', marginBottom: '8mm' }}>
                  {reportData.projectName}
                </p>
                <p style={{ fontSize: '11pt', color: '#4B5563', marginBottom: '10mm' }}>
                  Generated: {reportData.dateGenerated}
                </p>

                {/* Scope */}
                <p style={{ fontSize: '10pt', color: '#6B7280', marginBottom: '8mm' }}>
                  {reportData.hasFilters ? (
                    <>Analyzing {reportData.candidatesCount} of {reportData.totalCandidates} candidates • {reportData.companiesCount} of {reportData.totalCompanies} companies</>
                  ) : (
                    <>{reportData.candidatesCount} Candidates • {reportData.companiesCount} Companies</>
                  )}
                </p>

                {/* Additional Context */}
                {reportData.filterSummary && (
                  <div style={{ marginTop: '8mm' }}>
                    <p style={{ fontSize: '9pt', color: '#3B82F6', fontWeight: 'bold', marginBottom: '3mm' }}>
                      Filters Applied:
                    </p>
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => setAdditionalContext(e.currentTarget.textContent || '')}
                      style={{
                        fontSize: '8pt',
                        color: '#4B5563',
                        maxWidth: '150mm',
                        margin: '0 auto',
                        padding: '2mm',
                        borderRadius: '2mm',
                        cursor: 'text'
                      }}
                      className="editable-hover"
                    >
                      {reportData.filterSummary}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CHART PAGES */}
            {sections.filter(s => s.enabled).map((section, sectionIdx) => (
              <div key={section.chartKey} className="pdf-page bg-white shadow-lg" style={{ width: '210mm', minHeight: '297mm', padding: '15mm', position: 'relative' }}>
                {/* Top accent bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2mm', backgroundColor: '#3B82F6' }}></div>

                {/* Page header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10mm', fontSize: '8pt', color: '#9CA3AF' }}>
                  <span>Market Intelligence Report</span>
                  <span>{reportData.projectName}</span>
                </div>

                {/* Section title with accent bar */}
                <div className="section-header" style={{ marginBottom: '8mm', position: 'relative' }}>
                  <div style={{ width: '15mm', height: '0.8mm', backgroundColor: '#3B82F6', marginBottom: '3mm' }}></div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h2 style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1F2937' }}>
                      {section.title}
                    </h2>
                    <button
                      onClick={() => deleteSection(section.chartKey)}
                      className="delete-button"
                      title="Delete this section"
                    >
                      Delete Section
                    </button>
                  </div>
                </div>

                {/* Chart — rendered live (not as a screenshot) */}
                {section.chartData && section.chartData.length > 0 && (
                  <div style={{
                    backgroundColor: '#FAFAFB',
                    border: '0.3mm solid #E5E7EB',
                    borderRadius: '2mm',
                    padding: '5mm',
                    marginBottom: '8mm'
                  }}>
                    {section.chartType === 'doughnut' && (
                      <PremiumDoughnut
                        data={section.chartData}
                        centerLabel={section.centerLabel}
                        height={360}
                      />
                    )}
                    {section.chartType === 'bar' && (
                      <PremiumBarChart
                        data={section.chartData}
                        horizontal
                        height={Math.max(280, section.chartData.length * 36)}
                        multiColor
                      />
                    )}
                    {section.chartType === 'ranked' && (
                      <RankedList
                        data={section.chartData}
                        valueLabel="people"
                        maxItems={10}
                      />
                    )}
                  </div>
                )}

                {/* Insights */}
                <div>
                  <h3 style={{ fontSize: '10pt', fontWeight: 'bold', color: '#3B82F6', marginBottom: '4mm' }}>
                    Key Insights
                  </h3>
                  <div style={{ fontSize: '9pt', color: '#3C3C3C', lineHeight: '1.6' }}>
                    {section.insights.map((insight, insightIdx) => (
                      <div key={insightIdx} className="insight-row" style={{ display: 'flex', gap: '3mm', marginBottom: '3mm', alignItems: 'flex-start' }}>
                        <span style={{ color: '#3B82F6', flexShrink: 0 }}>•</span>
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => updateInsight(sectionIdx, insightIdx, e.currentTarget.textContent || '')}
                          style={{ flex: 1, cursor: 'text', padding: '1mm', borderRadius: '1mm' }}
                          className="editable-hover"
                        >
                          {insight}
                        </div>
                        <button
                          onClick={() => deleteInsight(sectionIdx, insightIdx)}
                          className="delete-button"
                          style={{ marginTop: '1mm' }}
                          title="Delete this insight"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  position: 'absolute',
                  bottom: '10mm',
                  left: '15mm',
                  right: '15mm',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '8pt',
                  color: '#9CA3AF'
                }}>
                  <span>Powered by MarketMapr</span>
                  <span>Page {sectionIdx + 2}</span>
                </div>
              </div>
            ))}

            {/* CAREER VELOCITY PAGE */}
            <div className="pdf-page bg-white shadow-lg" style={{ width: '210mm', minHeight: '297mm', padding: '15mm', position: 'relative' }}>
              {/* Top accent bar */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2mm', backgroundColor: '#3B82F6' }}></div>

              {/* Page header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10mm', fontSize: '8pt', color: '#9CA3AF' }}>
                <span>Market Intelligence Report</span>
                <span>{reportData.projectName}</span>
              </div>

              {/* Section title */}
              <div style={{ marginBottom: '10mm' }}>
                <div style={{ width: '15mm', height: '0.8mm', backgroundColor: '#3B82F6', marginBottom: '3mm' }}></div>
                <h2 style={{ fontSize: '16pt', fontWeight: 'bold', color: '#1F2937' }}>
                  Career Velocity & Progression
                </h2>
              </div>

              {/* Metrics */}
              <div style={{ marginBottom: '8mm' }}>
                {[
                  { label: 'Average Tenure', value: reportData.careerProgression.avgTenure },
                  { label: 'Average Experience', value: reportData.careerProgression.avgExperience },
                ].map((metric, idx) => (
                  <div key={idx} style={{
                    backgroundColor: '#FAFAFB',
                    border: '0.3mm solid #E5E7EB',
                    borderRadius: '2mm',
                    padding: '5mm',
                    marginBottom: '4mm',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '1mm', backgroundColor: '#3B82F6', borderRadius: '2mm 0 0 2mm' }}></div>
                    <p style={{ fontSize: '9pt', color: '#6B7280', marginBottom: '2mm' }}>{metric.label}</p>
                    <p style={{ fontSize: '16pt', fontWeight: 'bold', color: '#1F2937' }}>{formatCareerVelocity(metric.value)}</p>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div style={{
                position: 'absolute',
                bottom: '10mm',
                left: '15mm',
                right: '15mm',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '8pt',
                color: '#9CA3AF'
              }}>
                <span>Powered by MarketMapr</span>
                <span>Page {sections.filter(s => s.enabled).length + 2}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .editable-hover:hover {
          background-color: #F3F4F6;
          outline: 1px dashed #3B82F6;
        }
        .editable-hover:focus {
          background-color: #FEF3C7;
          outline: 2px solid #3B82F6;
        }
      `}</style>
    </div>
  );
};

export default ReportPreviewEditor;
